import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { listExternalOrders, type ExternalOrderRow } from '../api/externalOrders';
import { importExternalOrdersCsv, type ImportExternalOrdersResult } from '../api/adminExternalOrders';
import { ApiError } from '../api/client';
import { AddTicketModal } from './AddTicketModal';
import { SHIRT_COLORS } from '../types';

const PAGE_SIZE = 50;

type Props = { modal?: 'add' };

export function Tickets({ modal }: Props) {
  const [rows, setRows] = useState<ExternalOrderRow[]>([]);
  const [total, setTotal] = useState(0);
  const [used, setUsed] = useState(0);
  const [q, setQ] = useState('');
  const [loadError, setLoadError] = useState(false);
  const [offset, setOffset] = useState(0);
  const fileInput = useRef<HTMLInputElement>(null);
  const loadSeq = useRef(0);

  type PendingImport = { csv: string; preview: ImportExternalOrdersResult };

  const [pending, setPending] = useState<PendingImport | null>(null);
  const [importStatus, setImportStatus] = useState<
    { kind: 'error'; message: string } | { kind: 'done'; result: ImportExternalOrdersResult } | null
  >(null);
  const [previewing, setPreviewing] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const load = (query?: string, from = 0) => {
    // The search box debounces into this, so responses can land out of order. Without the guard a
    // slow failure could clear rows a newer successful request had already put on screen.
    const seq = ++loadSeq.current;
    listExternalOrders(query, { limit: PAGE_SIZE, offset: from })
      .then((r) => {
        if (seq !== loadSeq.current) return;
        setRows(r.externalOrders);
        setTotal(r.total);
        setUsed(r.used);
        setLoadError(false);
      })
      .catch(() => {
        if (seq !== loadSeq.current) return;
        // Keeping the previous rows would present them as current: a ticket whose order was
        // since cancelled would still read "nieużyty" with no sign the data is stale.
        setRows([]);
        setLoadError(true);
      });
  };

  // A new search starts at the top; page 4 of the old results says nothing about the new ones.
  useEffect(() => {
    setOffset(0);
  }, [q]);

  useEffect(() => {
    const t = setTimeout(() => load(q.trim() || undefined, offset), 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, offset]);

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPending(null);
    setImportStatus(null);
    const text = await file.text();
    if (fileInput.current) fileInput.current.value = '';

    setPreviewing(true);
    try {
      const preview = await importExternalOrdersCsv(text, { dryRun: true });
      setPending({ csv: text, preview });
    } catch (err) {
      setImportStatus({
        kind: 'error',
        message: importErrorMessage(err, 'Nie udało się sprawdzić pliku. Spróbuj ponownie.'),
      });
    } finally {
      setPreviewing(false);
    }
  };

  const confirmImport = async () => {
    if (!pending) return;
    setConfirming(true);
    try {
      const result = await importExternalOrdersCsv(pending.csv);
      setPending(null);
      setImportStatus({ kind: 'done', result });
      load(q.trim() || undefined, offset);
    } catch (err) {
      setImportStatus({
        kind: 'error',
        message: importErrorMessage(err, 'Import nie powiódł się. Spróbuj ponownie.'),
      });
    } finally {
      setConfirming(false);
    }
  };

  const cancelImport = () => {
    setPending(null);
    setImportStatus(null);
  };

  const firstRow = total === 0 ? 0 : offset + 1;
  const lastRow = Math.min(offset + PAGE_SIZE, total);
  const canPrev = offset > 0;
  const canNext = offset + PAGE_SIZE < total;

  return (
    <>
      <div
        className="card"
        style={{
          padding: 14,
          marginBottom: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          flexWrap: 'wrap',
        }}
      >
        <Stat label="Zamówień w bazie" value={loadError ? '—' : total} />
        <Stat label="Z zamówieniem" value={loadError ? '—' : used} accent />
        <Stat label="Bez zamówienia" value={loadError ? '—' : total - used} />

        <input
          className="input-a tickets-search"
          placeholder="Szukaj - numer zamówienia lub imię"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />

        <input
          ref={fileInput}
          type="file"
          accept=".csv,text/csv,text/plain"
          style={{ display: 'none' }}
          onChange={onFile}
        />
        <Link to="/admin/bilety/dodaj" className="btn-a primary" style={{ textDecoration: 'none' }}>
          + Dodaj zamówienie
        </Link>
        <button
          className="btn-a"
          onClick={() => fileInput.current?.click()}
          disabled={loadError || previewing}
          title={loadError ? 'Niedostępne, dopóki lista się nie wczyta' : undefined}
          style={loadError ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
        >
          {previewing ? 'Sprawdzam plik…' : '⇪ Importuj CSV'}
        </button>
      </div>

      {pending && (
        <div
          className="card"
          style={{
            padding: '12px 14px',
            marginBottom: 12,
            background: '#FFFAF0',
            borderColor: '#F0D9B8',
            fontSize: 13,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ flex: 1, minWidth: 240 }}>
            <div>
              Wczytano <b>{pending.preview.total}</b> zamówień z pliku
              {pending.preview.skipped > 0 ? ` (${pending.preview.skipped} pominiętych)` : ''}:
              nowych {pending.preview.added}, do aktualizacji {pending.preview.updated}, bez zmian{' '}
              {pending.preview.unchanged}.
            </div>
            {pending.preview.conflicts.length > 0 && (
              <div style={{ marginTop: 4, color: 'var(--orange-deep)' }}>
                ⚠ {pending.preview.conflicts.length} numerów zostanie bez zmian — są już
                wykorzystane do zamówień koszulek.
              </div>
            )}
            <div style={{ marginTop: 4, color: 'var(--muted)' }}>
              Numery spoza pliku zostaną nietknięte.
            </div>
          </div>
          <button className="btn-a" onClick={cancelImport} disabled={confirming}>
            Anuluj
          </button>
          <button className="btn-a primary" onClick={confirmImport} disabled={confirming}>
            {confirming ? 'Importuję…' : 'Potwierdź import'}
          </button>
        </div>
      )}

      {importStatus?.kind === 'error' && (
        <div
          className="card"
          style={{
            padding: '10px 14px',
            marginBottom: 12,
            background: '#FFF0E8',
            borderColor: '#F0C8B8',
            fontSize: 13,
            color: 'var(--orange-deep)',
          }}
        >
          {importStatus.message}
        </div>
      )}

      {importStatus?.kind === 'done' && (
        <div
          className="card"
          style={{
            padding: '10px 14px',
            marginBottom: 12,
            background: '#F0FAEF',
            borderColor: '#C8E0BD',
            fontSize: 13,
          }}
        >
          ✓ Dodano {importStatus.result.added}, zaktualizowano {importStatus.result.updated}, bez
          zmian {importStatus.result.unchanged}.
        </div>
      )}

      {importStatus?.kind === 'done' && importStatus.result.conflicts.length > 0 && (
        <div
          className="card"
          style={{
            padding: '10px 14px',
            marginBottom: 12,
            background: '#FFFAF0',
            borderColor: '#F0D9B8',
            fontSize: 13,
          }}
        >
          <div style={{ color: 'var(--orange-deep)', marginBottom: 6 }}>
            ⚠ {importStatus.result.conflicts.length} numerów zostało bez zmian — są już
            wykorzystane do zamówień koszulek. Popraw je ręcznie, jeśli plik ma rację.
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
            {importStatus.result.conflicts.map((x) => (
              <li key={x.externalId}>
                <b>{x.externalId}</b> — zostaje {x.firstName}, {x.ticketCount} bil. (zamówiono{' '}
                {x.orderedShirts} szt.); w pliku {x.incomingFirstName}, {x.incomingTicketCount} bil.
              </li>
            ))}
          </ul>
        </div>
      )}

      {loadError && (
        <div
          className="card"
          style={{
            padding: '10px 14px',
            marginBottom: 12,
            background: '#FFF0E8',
            borderColor: '#F0C8B8',
            fontSize: 13,
            color: 'var(--orange-deep)',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <span style={{ flex: 1, minWidth: 240 }}>
            Nie udało się wczytać listy. Sprawdź połączenie i spróbuj ponownie.
          </span>
          <button className="btn-a" onClick={() => load(q.trim() || undefined, offset)}>
            Spróbuj ponownie
          </button>
        </div>
      )}

      <div className="card" style={{ overflow: 'hidden' }}>
        <div className="a-table-scroll">
          <table className="tickets-table">
            <thead>
              <tr>
                <th style={{ width: 220 }}>Numer zamówienia</th>
                <th>Imię</th>
                <th style={{ width: 90 }}>Bilety</th>
                <th style={{ width: 190 }}>Koszulki</th>
                <th style={{ width: 180 }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="muted c-empty" style={{ textAlign: 'center', padding: 30 }}>
                    {loadError ? 'Brak danych do wyświetlenia.' : `Brak wyników${q ? ` dla "${q}"` : ''}.`}
                  </td>
                </tr>
              )}
              {rows.map((t) => (
                <tr key={t.externalId}>
                  <td className="mono c-num" style={{ fontWeight: 700 }}>
                    {t.externalId}
                  </td>
                  <td className="c-name">{t.firstName}</td>
                  <td className="mono c-count" data-label="Bilety">
                    {t.ticketCount}
                  </td>
                  <td className="mono c-shirts" data-label="Koszulki">
                    {t.orderedShirts} / {t.allowance}
                    <div className="muted" style={{ fontSize: 11 }}>
                      {SHIRT_COLORS.map(
                        (c) => `${c} ${t.byColor[c].alreadyOrdered}/${t.byColor[c].allowance}`,
                      ).join(' · ')}
                    </div>
                  </td>
                  <td className="c-status">
                    {t.remaining <= 0 ? (
                      <span className="pill paid">● limit wykorzystany</span>
                    ) : t.orderedShirts > 0 ? (
                      <span className="pill" style={{ background: '#FAE8C2', color: '#8C6B00' }}>
                        ● zostało {t.remaining}
                      </span>
                    ) : (
                      <span className="pill" style={{ background: '#F0EAD8', color: '#6B6358' }}>
                        ● bez zamówienia
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div
          style={{
            padding: '10px 14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderTop: '1px solid var(--admin-line)',
            gap: 10,
            flexWrap: 'wrap',
          }}
        >
          <span className="mono" style={{ fontSize: 12, color: 'var(--admin-muted)' }}>
            {total === 0 ? 'Brak wyników' : `${firstRow}–${lastRow} z ${total}`}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button
              type="button"
              className="btn-a"
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              disabled={!canPrev}
              style={{
                padding: '5px 10px',
                fontSize: 12,
                opacity: canPrev ? 1 : 0.4,
                cursor: canPrev ? 'pointer' : 'not-allowed',
              }}
            >
              ← Poprzednia
            </button>
            <span
              className="mono"
              style={{ fontSize: 12, color: 'var(--admin-muted)', padding: '0 6px' }}
            >
              Strona {Math.floor(offset / PAGE_SIZE) + 1} z {Math.max(1, Math.ceil(total / PAGE_SIZE))}
            </span>
            <button
              type="button"
              className="btn-a"
              onClick={() => setOffset(offset + PAGE_SIZE)}
              disabled={!canNext}
              style={{
                padding: '5px 10px',
                fontSize: 12,
                opacity: canNext ? 1 : 0.4,
                cursor: canNext ? 'pointer' : 'not-allowed',
              }}
            >
              Następna →
            </button>
          </div>
        </div>
      </div>
      {modal === 'add' && <AddTicketModal onAdded={() => load(q.trim() || undefined, offset)} />}
    </>
  );
}

function importErrorMessage(err: unknown, fallback: string): string {
  const message = err instanceof ApiError ? err.body.message : undefined;
  return typeof message === 'string' ? message : fallback;
}

function Stat({ label, value, accent }: { label: string; value: number | string; accent?: boolean }) {
  return (
    <div>
      <div
        className="mono"
        style={{ fontSize: 10, letterSpacing: '.14em', color: 'var(--admin-muted)' }}
      >
        {label.toUpperCase()}
      </div>
      <div
        className="display"
        style={{ fontSize: 28, lineHeight: 1, color: accent ? 'var(--orange-deep)' : 'var(--admin-ink)' }}
      >
        {value}
      </div>
    </div>
  );
}
